import FinancingHelper from "../../helpers/FinancingHelper";

export default class DCFValuation {
  constructor({
    dealType,
    yearsFormatted = [],
    equityDiscountRate,
    terminalGrowthRate,
    FCFE = [],
    debtArray = [],
    repaymentArray = [],
    EBITDA,
    EBIT,
    ownerFinancing,
    lineOfCredit,
    otherDebtsArray,
    startCash,
    endCash,
    securities,
    startDebt,
    reinvestRate
  }) {
    this.dealType = dealType;
    this.yearsFormatted = yearsFormatted;
    this.equityDiscountRate = equityDiscountRate;
    this.terminalGrowthRate = terminalGrowthRate;
    this.FCFE = FCFE;
    this.debtArray = debtArray;
    this.repaymentArray = repaymentArray;
    this.EBITDA = EBITDA;
    this.EBIT = EBIT;
    this.ownerFinancing = ownerFinancing;
    this.lineOfCredit = lineOfCredit;
    this.otherDebtsArray = otherDebtsArray;
    this.startCash = startCash;
    this.endCash = endCash;
    this.securities = securities;
    this.startDebt = startDebt;
    this.reinvestRate = reinvestRate;
  }

  get terminalValue() {
    const { yearsFormatted, FCFE, equityDiscountRate, terminalGrowthRate } = this;
    const last = yearsFormatted.length - 1;

    return yearsFormatted.map((year, index) => ({
      date: year,
      value: index === last ? FCFE[last].value * (1 + terminalGrowthRate) / (equityDiscountRate - terminalGrowthRate) || 0 : 0,
      disabled: index === last ? false : true,
    }))
  }

  get totalFCFE() {
    return this.getArraySum(this.FCFE, this.terminalValue);
  }

  get cumulativeCashFlows() {
    const { totalFCFE } = this;

    return totalFCFE.map((obj, index) => {
      let sum = 0;
      for(let i = 0; i <= index; i++) sum += totalFCFE[i].value;
      return {date: obj.year, value: sum, disabled: false};
    });
  }

  get PVFactor() {
    const { yearsFormatted, equityDiscountRate } = this;

    return yearsFormatted.map((year, index) => ({
      date: year,
      value: Math.pow(1 / (1 + equityDiscountRate), index),
      disabled: false,
    }));
  }

  get NPVfromFirstPrincipal() {
    return this.getArraySum(this.totalFCFE, this.PVFactor, { multi: true });
  }

  get debtToEBITDA() {
    return this.debtArray.map((debt) => ({ ...debt, value: debt.value / this.EBITDA }));
  }

  get debtToEBIT() {
    return this.debtArray.map((debt) => ({ ...debt, value: debt.value / this.EBIT }));
  }

  get EBITDAToRepayment() {
    return this.repaymentArray.map((repayment) => ({
      ...repayment,
      value: this.EBITDA / repayment.value,
      disabled: repayment.value === 0 ? true : false
    }));
  }

  get EBITToRepayment() {
    return this.repaymentArray.map((repayment) => ({
      ...repayment,
      value: this.EBIT / repayment.value,
      disabled: repayment.value === 0 ? true : false
    }));
  }

  get debtMetrics() {
    const { ownerFinancing, lineOfCredit, otherDebtsArray } = this;
    ownerFinancing.IRR = FinancingHelper.XIRR(ownerFinancing.cashFlowArray) * 100;
    ownerFinancing.EM = FinancingHelper.EM(ownerFinancing.cashFlowArray);
    lineOfCredit.IRR = FinancingHelper.XIRR(lineOfCredit.cashFlowArray) * 100;
    lineOfCredit.EM = FinancingHelper.EM(lineOfCredit.cashFlowArray);
    otherDebtsArray.map((debt) => {
      debt.IRR = FinancingHelper.XIRR(debt.cashFlowArray) * 100;
      debt.EM = FinancingHelper.EM(debt.cashFlowArray);
    })

    return { ownerFinancing, lineOfCredit, otherDebtsArray };
  }

  get enterpriseValue() {
    return this.NPVfromFirstPrincipal.reduce((acc, obj) => acc + obj.value, 0);
  }

  get startEquityValue() {
    return this.enterpriseValue + this.startCash + this.securities - this.startDebt;
  }

  get endDebt() {
    return this.debtArray[this.debtArray.length - 1].value;
  }

  get endEquityValue() {
    return this.enterpriseValue + this.endCash + this.securities - this.endDebt;
  }

  get XIRR() {
    return FinancingHelper.XIRR(this.totalFCFE) * 100;
  }

  get MIRR() {
    return FinancingHelper.MIRR(this.totalFCFE, this.equityDiscountRate, this.reinvestRate) * 100;
  }

  get cashOnReturn() {
    return this.totalFCFE.reduce((acc, obj) => acc + obj.value, 0) / Math.abs(this.totalFCFE[0].value) + 1;
  }

  get percentTV() {
    const { terminalValue, FCFE } = this;
    const finalValue = terminalValue[terminalValue.length - 1].value;
    return finalValue / (FCFE.reduce((acc, obj, index) => acc + (index > 0 ? obj.value : 0), 0) + finalValue) * 100;
  }

  get percentAC() {
    return 100 - this.percentTV;
  }

  get payback() {
    const { totalFCFE, cumulativeCashFlows } = this;
    const len = cumulativeCashFlows.length;
    let sum = 0;

    for(let i = 1; i < len; i++) {
      if(cumulativeCashFlows[i].value <= 0) sum++;
      else {
        sum += cumulativeCashFlows[i].value / totalFCFE[i].value;
        break;
      }
    }
    return sum;
  }
  
  get profitability() {
    return this.NPVfromFirstPrincipal.reduce((acc, obj) => acc + obj.value, 0) / Math.abs(this.totalFCFE[0].value) + 1;
  }

  getArraySum(array1, array2, options = { multi: false }) {
    //add element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        ({
          date: year,
          value: options.multi
            ? Number(array1[index].value) * Number(array2[index].value)
            : Number(array1[index].value) + Number(array2[index].value),
          disabled: (array1[index].disabled && array2[index].disabled) || false,
        })
    );
  }
}
